import React, { useState } from 'react';
import PageHeader from '../components/PageHeader';
import SectionHeading from '../components/ui/SectionHeading';
import Reveal from '../components/ui/Reveal';
import { faqs } from '../data/content';
import { categories, showroom } from '../data/collections';
import { brand, site } from '../data/site';
import usePageMeta from '../hooks/usePageMeta';
import {
  IconClock,
  IconFacebook,
  IconInstagram,
  IconMail,
  IconPhone,
  IconPin,
  IconPlus,
  IconWhatsApp,
} from '../components/ui/Icons';

const DIGITS = /\D/g;

function ContactForm() {
  const [form, setForm] = useState({ name: '', mobile: '', interest: '', message: '' });
  const [errors, setErrors] = useState({});

  const update = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((p) => ({ ...p, [k]: undefined }));
  };

  const submit = (e) => {
    e.preventDefault();
    const next = {};
    if (!form.name.trim()) next.name = 'Please enter your name.';
    if (form.mobile.replace(DIGITS, '').length < 10) next.mobile = 'Enter a 10-digit mobile number.';

    setErrors(next);
    if (Object.keys(next).length) return;

    const lines = [
      `Namaste ${site.name} 🙏`,
      '',
      `Name: ${form.name.trim()}`,
      `Mobile: ${form.mobile.replace(DIGITS, '')}`,
      form.interest && `Interested in: ${form.interest}`,
      form.message && `Message: ${form.message.trim()}`,
    ].filter(Boolean);

    window.open(
      `https://wa.me/${site.whatsapp}?text=${encodeURIComponent(lines.join('\n'))}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  return (
    <form onSubmit={submit} noValidate className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="field-label" htmlFor="c-name">Your name *</label>
          <input id="c-name" className="field" value={form.name} onChange={update('name')} placeholder="Full name" />
          {errors.name && <p className="mt-1.5 text-xs text-red-700">{errors.name}</p>}
        </div>
        <div>
          <label className="field-label" htmlFor="c-mobile">Mobile *</label>
          <input
            id="c-mobile"
            type="tel"
            inputMode="numeric"
            maxLength={11}
            className="field"
            value={form.mobile}
            onChange={update('mobile')}
            placeholder="10-digit number"
          />
          {errors.mobile && <p className="mt-1.5 text-xs text-red-700">{errors.mobile}</p>}
        </div>
      </div>

      <div>
        <label className="field-label" htmlFor="c-interest">What are you looking for?</label>
        <select id="c-interest" className="field" value={form.interest} onChange={update('interest')}>
          <option value="">Select…</option>
          {categories.map((c) => (
            <option key={c.slug} value={c.name}>{c.name}</option>
          ))}
          <option value="Custom / bespoke order">Custom / bespoke order</option>
          <option value="Old gold exchange">Old gold exchange</option>
          <option value="Repair & re-polish">Repair &amp; re-polish</option>
        </select>
      </div>

      <div>
        <label className="field-label" htmlFor="c-message">Message</label>
        <textarea
          id="c-message"
          rows={4}
          className="field resize-none"
          value={form.message}
          onChange={update('message')}
          placeholder="Occasion, date, budget, or a design you have in mind…"
        />
      </div>

      <button type="submit" className="btn-gold w-full">
        <IconWhatsApp solid className="h-4 w-4" />
        Send on WhatsApp
      </button>

      <p className="text-center text-[13px] font-light text-royal/70">
        Opens WhatsApp with your details filled in — no data is stored on this site.
      </p>
    </form>
  );
}

function Faq({ item, open, onToggle }) {
  return (
    <div className="border-b border-royal/10">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-start justify-between gap-6 py-5 text-left"
      >
        <span className={`font-display text-lg leading-snug transition-colors ${open ? 'text-gold-600' : 'text-royal'}`}>
          {item.q}
        </span>
        <IconPlus
          className={`mt-1 h-4 w-4 shrink-0 transition-transform duration-500 ease-luxe ${
            open ? 'rotate-45 text-gold' : 'text-royal/80'
          }`}
        />
      </button>
      <div
        className="grid transition-all duration-500 ease-luxe"
        style={{ gridTemplateRows: open ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          <p className="pb-5 pr-10 text-sm font-light leading-relaxed text-royal/80">{item.a}</p>
        </div>
      </div>
    </div>
  );
}

export default function Contact() {
  const [openFaq, setOpenFaq] = useState(0);

  usePageMeta({
    title: 'Contact & Visit | Pramukh Jewellers, Science City Road, Ahmedabad',
    description: `Visit Pramukh Jewellers at ${site.address.full}. Call ${site.phoneDisplay} or message us on WhatsApp.`,
    path: '/contact',
  });

  const details = [
    {
      icon: IconPin,
      label: 'Showroom',
      value: (
        <a href={site.social.maps} target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-gold-600">
          {site.address.line1}, {site.address.line2},<br />
          {site.address.city}, {site.address.state} {site.address.pin}
        </a>
      ),
    },
    {
      icon: IconPhone,
      label: 'Office',
      value: (
        <a href={`tel:${site.phone}`} className="transition-colors hover:text-gold-600">
          {site.phoneDisplay}
        </a>
      ),
    },
    {
      icon: IconMail,
      label: 'Email',
      value: (
        <a href={`mailto:${site.email}`} className="break-all transition-colors hover:text-gold-600">
          {site.email}
        </a>
      ),
    },
    {
      icon: IconClock,
      label: 'Hours',
      value: site.hours.map((h) => (
        <span key={h.days} className="block">
          {h.days} · <span className="text-royal">{h.time}</span>
        </span>
      )),
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Get in touch"
        title="Visit us"
        lede="Fortune Business Hub on Science City Road, Sola. Six days a week — come and see the weight in your hand."
        image={showroom.tryOn}
        crumbs={[{ label: 'Home', to: '/' }, { label: 'Contact' }]}
      />

      {/* Details + form */}
      <section className="bg-cream py-20 lg:py-28">
        <div className="shell grid gap-14 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
          <div>
            <SectionHeading align="left" eyebrow="Where to find us" title="The showroom" className="lg:mx-0" />

            <div className="mt-9 space-y-7">
              {details.map(({ icon: Icon, label, value }) => (
                <Reveal key={label} className="flex gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-royal/15 text-gold">
                    <Icon className="h-4 w-4" />
                  </span>
                  <div>
                    <p className="field-label mb-1">{label}</p>
                    <div className="text-sm font-light leading-relaxed text-royal/75">{value}</div>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={140} className="mt-9 flex items-center gap-3 border-t border-royal/10 pt-8">
              <a
                href={site.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="flex h-11 w-11 items-center justify-center border border-royal/15 text-royal/75 transition-all hover:border-gold hover:bg-gold hover:text-royal"
              >
                <IconInstagram className="h-4 w-4" />
              </a>
              <a
                href={site.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="flex h-11 w-11 items-center justify-center border border-royal/15 text-royal/75 transition-all hover:border-gold hover:bg-gold hover:text-royal"
              >
                <IconFacebook className="h-4 w-4" />
              </a>
              <span className="ml-2 text-[13px] uppercase tracking-wider2 text-royal/80">Follow the new arrivals</span>
            </Reveal>

            <Reveal delay={200} className="mt-9 flex items-center gap-5 border border-royal/10 bg-ivory p-6">
              <img src={brand.qrInstagram} alt="Scan to open our Instagram" className="h-24 w-24" />
              <img src={brand.qrLocation} alt="Scan for showroom directions" className="h-24 w-24" />
              <p className="text-xs font-light leading-relaxed text-royal/70">
                Scan for Instagram
                <br />
                and directions.
              </p>
            </Reveal>
          </div>

          <Reveal delay={100} className="border border-royal/10 bg-ivory p-7 sm:p-9">
            <span className="eyebrow text-gold-600">
              <span className="h-px w-6 bg-gold/40" />
              Send a message
            </span>
            <h2 className="mt-4 font-display text-[1.9rem] leading-tight text-royal sm:text-[2.3rem]">
              Tell us what you are looking for
            </h2>
            <p className="mt-3 text-sm font-light leading-relaxed text-royal/75">
              We will shortlist pieces and keep them aside for your visit.
            </p>
            <div className="mt-7">
              <ContactForm />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Map — full-bleed, full colour and zoomed tight on the building */}
      <section className="border-y border-royal/10 bg-ivory pb-16 pt-4 lg:pb-20">
        <div className="shell">
          <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
            <div className="flex items-center gap-4">
              <span className="h-px w-10 bg-gold/50" />
              <span className="text-[12px] font-medium uppercase tracking-luxe text-royal/70">
                Find the showroom
              </span>
            </div>
            <a
              href={site.social.maps}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-line"
            >
              <span className="btn-line-rule" />
              Open in Google Maps
            </a>
          </div>

          <div className="relative h-[460px] w-full border border-royal/10 bg-beige sm:h-[540px] lg:h-[680px]">
            <iframe
              title="Pramukh Jewellers on Google Maps"
              src={site.mapEmbed}
              className="absolute inset-0 h-full w-full"
              style={{ border: 0 }}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>

          <p className="mt-4 text-[12.5px] font-light text-royal/70">
            {site.address.full}
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-ivory py-20 lg:py-28">
        <div className="shell max-w-3xl">
          <SectionHeading eyebrow="Questions" title="Before you visit" />

          <div className="mt-12">
            {faqs.map((f, i) => (
              <Faq key={f.q} item={f} open={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? -1 : i)} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
